/**
 * 제품으로 건너가는 다리
 * ---------------------------------------------------------------------------
 * 제품 웹이 토큰이 없을 때 여기로 보낸다.
 *
 *   {제품}/…  →  {콘솔}/sso?product=eco&next={돌아갈 주소}
 *              →  (로그인 안 되어 있으면 /login 을 거친 뒤 여기로)
 *              →  1회용 코드를 받아 제품으로 되돌려보낸다
 *              →  제품이 코드를 자기 토큰으로 바꾼다
 *
 * 왜 이 단계가 필요한가
 *   제품이 콘솔의 refresh 쿠키를 그대로 쓰면 안 된다. 그 토큰은 1회용으로
 *   회전하는데, 두 사이트가 같은 쿠키를 공유하면 나중에 갱신한 쪽이 이미 쓴
 *   토큰을 내밀게 되고, 플랫폼은 그것을 탈취로 보고 **세션을 전부 끊는다.**
 *   두 사이트를 같이 열면 반드시 일어난다. 실제로 그래서 양쪽 다 로그인이 풀렸다.
 *
 *   콘솔은 액세스 토큰을 메모리에 갖고 있다. 그것으로 코드를 받아 넘기면
 *   쿠키를 건드리지 않는다. 제품은 자기 체인의 쿠키를 새로 받는다.
 *
 * 코드는 URL 프래그먼트(#)로 넘긴다
 *   프래그먼트는 서버로 전송되지 않는다. 쿼리로 넘기면 액세스 로그와
 *   Referer 에 코드가 남는다. 60초짜리라도 남길 이유가 없다.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Result, Space, Spin } from 'antd';
import { apiClient } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

/** 로그인 화면과 같은 규칙. 허용 목록에 없는 주소로는 보내지 않는다. */
function allowedTarget(next: string | null): string | null {
  if (!next) return null;

  const allowed = (import.meta.env.VITE_PRODUCT_ORIGINS ?? '')
    .split(',')
    .map((o: string) => o.trim())
    .filter(Boolean);

  try {
    const url = new URL(next);
    return allowed.includes(url.origin) ? url.toString() : null;
  } catch {
    return null;
  }
}

export default function SsoBridgePage() {
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  const productCode = params.get('product') ?? '';
  const next = params.get('next');

  // StrictMode 는 효과를 두 번 실행한다. 코드는 1회용이라 두 번 받으면
  // 앞의 것이 버려진 채 남는다. 한 번만 돌게 막는다.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const target = allowedTarget(next);

    if (!productCode) {
      setError('제품이 지정되지 않았습니다.');
      return;
    }
    if (!target) {
      setError('허용되지 않은 주소입니다. 관리자에게 문의해 주세요.');
      return;
    }

    void (async () => {
      try {
        /*
         * redirectUri 를 함께 보낸다.
         *
         * 아래 allowedTarget() 검사는 화면에서만 하는 것이라, 목록이 번들에
         * 구워져 있고 제품별로 갈려 있지도 않았다. 이제 서버가 제품에 등록된
         * base_url 과 대조한다. 화면 검사는 빨리 알려 주는 용도로 남겨 둔다.
         */
        const { data } = await apiClient.post<{ code: string; expiresIn: number }>(
          '/auth/sso/code',
          { productCode, redirectUri: target },
        );

        const url = new URL(target);
        url.hash = `sso_code=${encodeURIComponent(data.code)}`;
        window.location.replace(url.toString());
      } catch (err) {
        setError(errorText(err as never));
      }
    })();
  }, [productCode, next]);

  if (error) {
    return (
      <Result
        status="warning"
        title="제품으로 이동하지 못했습니다"
        subTitle={error}
        extra={
          <Space>
            <Button type="primary" onClick={() => window.location.reload()}>
              다시 시도
            </Button>
            <Button onClick={() => window.location.assign('/app')}>콘솔로</Button>
          </Space>
        }
      />
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
      <Space direction="vertical" align="center" size={16}>
        <Spin size="large" />
        <Alert type="info" showIcon message="제품으로 이동하는 중입니다" />
      </Space>
    </div>
  );
}
